import { useMemo, useState } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { interactiveFloorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { getComboAwareTableIds } from '@shared/floorPlans/comboFloorPlanTables';
import { FloorPlanData, FloorPlanTableData } from '@shared/types/floorPlans';
import type { BookingUnit } from '@shared/types/restaurants';
import { centsToWholeDollar } from '@shared/utils/currency';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import { type TierTablePricing } from './apiHelpers';
import { getBookingUnitLabels } from '@shared/restaurantEvents/bookingUnitLabels';
import styles from './RestaurantEventTiersList.scss';

interface TableSelectionFieldsProps {
  floorPlans: FloorPlanData[];
  selectedFloorPlanId: string | undefined;
  onSelectFloorPlanId: (floorPlanId: string) => void;
  availableTables: TierTablePricing[];
  selectedFloorPlanTable: FloorPlanTableData | undefined;
  onSelectTable: (table: FloorPlanTableData) => void;
  onConfirm?: (selectedFloorPlanTable: FloorPlanTableData) => void;
  onSkip?: () => void;
  guestCount: number;
  bookingUnit: BookingUnit;
  /**
   * Shown as a status row above the floor plan on the very first load of
   * `availableTables` for the current tier, so an empty response mid-flight
   * doesn't read as "sold out" -- the floor plan itself still always renders
   * underneath, all tables disabled either way.
   */
  isLoadingAvailability?: boolean;
  /**
   * The inline accordion usage on the single-page ticket panel selects a
   * table as soon as it's tapped, but leaves the accordion open so guests
   * can see or change their pick -- it doesn't need a separate "no table
   * selected" card or an "Upgrade for $X" / "Skip Table Upgrade"
   * confirmation step the way the legacy modal does.
   */
  hideActions?: boolean;
  /**
   * `.floorPlanWrapper` (in `RestaurantEventTiersList.scss`) hardcodes a
   * 40rem desktop width sized for the legacy centered `Modal`. The inline
   * accordion usage on the single-page ticket panel lives in a much
   * narrower column, so it needs to override that with an explicit inline
   * style (which always wins over the class, regardless of stylesheet
   * order) rather than being stuck at a fixed width wider than its column
   * -- that was what forced the whole panel to grow sideways when opened.
   */
  floorPlanWidth?: string;
}

/**
 * The floor-plan-picker + selected-table summary shared by both the legacy
 * table-selection modal (`TableSelectionModal`) and the single-page ticket
 * purchase panel's inline accordion -- the two callers only differ in the
 * chrome wrapping this content (a `Modal` vs a `ChooseTableAccordionCard`),
 * not in how a table is browsed, selected, or confirmed/skipped.
 */
export const TableSelectionFields = ({
  floorPlans,
  selectedFloorPlanId,
  onSelectFloorPlanId,
  availableTables,
  selectedFloorPlanTable,
  onSelectTable,
  onConfirm,
  onSkip = () => {},
  guestCount,
  bookingUnit,
  isLoadingAvailability = false,
  hideActions = false,
  floorPlanWidth,
}: TableSelectionFieldsProps) => {
  const { unitLabel, unitLabelCapitalized } = getBookingUnitLabels(bookingUnit);
  const [hoveredFloorPlanTableId, setHoveredFloorPlanTableId] = useState<
    string | null
  >(null);

  const selectedFloorPlan =
    floorPlans.find((fp) => fp.id === selectedFloorPlanId) ?? floorPlans[0];

  const selectedTablePricing = availableTables.find(
    (tp) => tp.floorPlanTableId === selectedFloorPlanTable?.id,
  );
  const selectedTablePrice = selectedTablePricing?.price ?? 0;
  const selectedTablePublicName = selectedTablePricing?.publicName?.trim();

  const highlightedTableIds = useMemo(() => {
    if (!selectedFloorPlanTable) {
      return [];
    }

    const floorPlanTables = selectedFloorPlan?.floorPlanTables ?? [];
    const activeListingIds = availableTables
      .filter((tp) => tp.floorPlanTableId === selectedFloorPlanTable.id)
      .map((tp) => tp.listingId);

    return getComboAwareTableIds(
      floorPlanTables,
      selectedFloorPlanTable,
      activeListingIds,
      guestCount,
    );
  }, [availableTables, guestCount, selectedFloorPlan, selectedFloorPlanTable]);

  const hoveredComboTableIds = useMemo(() => {
    if (!hoveredFloorPlanTableId) {
      return [];
    }

    const floorPlanTables = selectedFloorPlan?.floorPlanTables ?? [];
    const hoveredTable = floorPlanTables.find(
      (table) => table.id === hoveredFloorPlanTableId,
    );

    const isHoveredTableSelectable = hoveredTable?.listings.some((listing) =>
      availableTables.some((tp) => tp.listingId === listing.id),
    );

    if (!hoveredTable || !isHoveredTableSelectable) {
      return [];
    }

    const activeListingIds = availableTables
      .filter((tp) => tp.floorPlanTableId === hoveredTable.id)
      .map((tp) => tp.listingId);

    return getComboAwareTableIds(
      floorPlanTables,
      hoveredTable,
      activeListingIds,
      guestCount,
    );
  }, [availableTables, guestCount, hoveredFloorPlanTableId, selectedFloorPlan]);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div className={styles.floorPlanSelectionContainer}>
        <FloorPlanSelector
          floorPlans={floorPlans}
          selectedFloorPlan={selectedFloorPlan}
          onChange={onSelectFloorPlanId}
        />
      </div>
      {isLoadingAvailability && (
        <div className={styles.availabilityLoadingRow} role="status">
          <span className={typography.t2}>
            Checking {unitLabel} availability…
          </span>
        </div>
      )}
      {selectedFloorPlan && (
        <div
          className={styles.floorPlanWrapper}
          style={floorPlanWidth ? { width: floorPlanWidth } : undefined}
        >
          <FloorPlan
            backgroundSrc={selectedFloorPlan.backgroundSrc}
            floorPlanTablesRenderer={interactiveFloorPlanTablesRendererFactory<FloorPlanTableData>(
              {
                calculateIsComboHovered: (table) =>
                  hoveredComboTableIds.includes(table.id),
                calculateIsHighlighted: (table) =>
                  highlightedTableIds.includes(table.id),
                calculateIsSelectable: (floorPlanTable) =>
                  floorPlanTable.listings.some((listing) =>
                    availableTables.some((tp) => tp.listingId === listing.id),
                  ),
                handleTableOnClick: (table) => {
                  onSelectTable(table);
                },
                handleTableOnMouseEnter: (table) => {
                  setHoveredFloorPlanTableId(table.id);
                },
                handleTableOnMouseLeave: () => {
                  setHoveredFloorPlanTableId(null);
                },
                tables: selectedFloorPlan.floorPlanTables,
                unitLabel,
              },
            )}
          ></FloorPlan>
        </div>
      )}
      {!hideActions && (
        <div className={styles.tableSelectionModalActions}>
          {!selectedFloorPlanTable && (
            <div className={styles.noTableSelectedCard}>
              <Icon name="table" />
              <div className={styles.noTableSelectedTextContent}>
                <span className={typography.c3_20}>
                  Tap a {unitLabel} for this event upgrade
                </span>
                <span className={typography.t2}>
                  {unitLabelCapitalized} prices may vary
                </span>
              </div>
            </div>
          )}
          {selectedFloorPlanTable && (
            <div className={styles.selectedTableSection}>
              <section>
                <div>
                  <Icon name={selectedFloorPlanTable.iconName} />
                  <span className={cx(typography.c3_20)}>
                    {selectedTablePublicName ||
                      `${unitLabelCapitalized} ${selectedFloorPlanTable.name}`}{' '}
                    selected
                  </span>
                </div>
                <span className={styles.price}>
                  {centsToWholeDollar(selectedTablePrice)}
                </span>
              </section>
              <Button
                variant={ButtonVariants.Primary}
                onClick={() => onConfirm?.(selectedFloorPlanTable)}
                label={`Upgrade for ${centsToWholeDollar(selectedTablePrice)}`}
              />
            </div>
          )}
          <Button
            variant={ButtonVariants.Tertiary}
            label={`Skip ${unitLabelCapitalized} Upgrade`}
            className={styles.skipTableUpgradeButton}
            onClick={onSkip}
          />
        </div>
      )}
    </div>
  );
};
